import { dom } from "../dom";
import { HIDDEN_GHOST_ID, PICK_CUSTOM_ID, state } from "../state";
import { fragmentsRaycast } from "../fragments/ifc-shim";
import {
  getActiveCamera,
  ghostMaterial,
  highlightMaterial,
  ifcLoader,
  pointerNdc,
  scene,
} from "../viewer/scene";
import { setStatus } from "../ui/status";
import { isPassingFilters, rebuildMeshVisibility } from "../ifc/filters";
import { getGeometryExpressIds } from "../fragments/helpers";
import { getAdskNameValue, getIfcName } from "../ifc/props";
import { updateAnnotationUi } from "./annotations";
import { refreshPropertiesPanel } from "./properties";
import { renderIfcTree } from "./tree";
import { onLangChange, t } from "../i18n";

/**
 * Кнопка «Мультивыбор» — замена Ctrl+клик на устройствах без клавиатуры
 * (тач-планшеты, интерактивные панели). Пока режим включён, обычный клик/тап
 * по элементу добавляет/убирает его из текущего выделения вместо замены.
 */
export function updateMultiSelectUi(): void {
  dom.multiselectToggleButton.classList.toggle("is-active", state.multiSelectMode);
  dom.multiselectToggleButton.textContent = state.multiSelectMode
    ? t("btn.multiselectOn")
    : t("btn.multiselect");
}

export function toggleMultiSelectMode(): void {
  state.multiSelectMode = !state.multiSelectMode;
  updateMultiSelectUi();
  setStatus(
    state.multiSelectMode
      ? t("status.multiselectOn")
      : t("status.multiselectOff"),
  );
}

export function setSelectionCaption(text: string | null): void {
  const empty = !text;
  dom.selectionInfoElement.textContent = empty ? "" : text;
  dom.selectionInfoElement.classList.toggle("is-empty", empty);
  dom.selectionInfoElement.toggleAttribute("hidden", empty);
}

onLangChange(() => {
  updateMultiSelectUi();
  if (state.selectedExpressIds.size > 0) {
    void refreshSelectionInfo();
  } else {
    setSelectionCaption(null);
  }
});

export function updateVisibilityButtons(): void {
  dom.hideSelectedButton.disabled = !state.ifcModel || state.selectedExpressIds.size === 0;
  dom.isolateSelectedButton.disabled = !state.ifcModel || state.selectedExpressIds.size === 0;
  dom.showAllHiddenButton.disabled = !state.ifcModel || state.hiddenExpressIds.size === 0;
  dom.screenshotButton.disabled = !state.ifcModel;
  dom.shareLinkBtn.disabled = !state.ifcModel;
}

export function clearSelection(): void {
  state.selectedExpressIds.clear();
  updateVisibilityButtons();
  updateAnnotationUi();
  if (!state.ifcModel) {
    setSelectionCaption(null);
    void refreshPropertiesPanel();
    renderIfcTree();
    return;
  }
  try {
    ifcLoader.ifcManager.removeSubset(state.ifcModel.modelID, highlightMaterial, PICK_CUSTOM_ID);
  } catch (_) {
    /* ignore */
  }
  setSelectionCaption(null);
  void refreshPropertiesPanel();
  renderIfcTree();
}

export function refreshHiddenGhost(): void {
  if (!state.ifcModel) {
    return;
  }
  try {
    ifcLoader.ifcManager.removeSubset(state.ifcModel.modelID, ghostMaterial, HIDDEN_GHOST_ID);
  } catch (_) {
    /* ignore */
  }
  if (state.hiddenExpressIds.size === 0) {
    return;
  }
  ghostMaterial.opacity = 0.15;
  ghostMaterial.transparent = true;
  ghostMaterial.depthWrite = false;
  ghostMaterial.needsUpdate = true;
  ifcLoader.ifcManager.createSubset({
    modelID: state.ifcModel.modelID,
    ids: Array.from(state.hiddenExpressIds),
    material: ghostMaterial,
    scene,
    removePrevious: true,
    customID: HIDDEN_GHOST_ID,
  });
}

export function updatePickHighlight(): void {
  if (!state.ifcModel || state.selectedExpressIds.size === 0) {
    return;
  }
  try {
    ifcLoader.ifcManager.removeSubset(state.ifcModel.modelID, highlightMaterial, PICK_CUSTOM_ID);
  } catch (_) {
    /* ignore */
  }
  ifcLoader.ifcManager.createSubset({
    modelID: state.ifcModel.modelID,
    ids: Array.from(state.selectedExpressIds),
    material: highlightMaterial,
    scene,
    removePrevious: true,
    customID: PICK_CUSTOM_ID,
  });
}

export async function refreshSelectionInfo(): Promise<void> {
  if (!state.ifcModel || state.selectedExpressIds.size === 0) {
    setSelectionCaption(null);
    return;
  }
  if (state.selectedExpressIds.size === 1) {
    const expressId = state.selectedExpressIds.values().next().value as number;
    let type = "Unknown";
    let elementName = "Без имени";
    let adskName = "—";
    try {
      const props = await ifcLoader.ifcManager.getItemProperties(
        state.ifcModel.modelID,
        expressId,
        false,
      );
      // Тип берём из props (_category из getItemsData), getItem().category у
      // Fragments не заполняется лениво → getIfcType возвращал пусто.
      const propsType = typeof (props as { type?: unknown })?.type === "string"
        ? ((props as { type?: string }).type as string).trim()
        : "";
      const metaType = state.elementMeta.get(expressId)?.type || "";
      type = propsType || metaType || "Unknown";
      elementName = getIfcName(props);
      adskName = await getAdskNameValue(state.ifcModel.modelID, expressId);
    } catch (_) {
      /* keep fallback */
    }
    setSelectionCaption(
      `${t("selection.one", { count: 1 })}\n` +
        `${t("selection.id")}: ${expressId}\n` +
        `${t("selection.type")}: ${type}\n` +
        `${t("selection.name")}: ${elementName}\n` +
        `${t("selection.adsk")}: ${adskName}`,
    );
    return;
  }
  const sorted = [...state.selectedExpressIds].sort((a, b) => a - b);
  const maxShow = 14;
  const head = sorted.slice(0, maxShow).join(", ");
  const more =
    sorted.length > maxShow
      ? `\n${t("selection.more", { count: sorted.length - maxShow })}`
      : "";
  setSelectionCaption(
    `${t("selection.many", { count: sorted.length })}\n` +
      `${t("selection.ids")}: ${head}${more}`,
  );
}

export async function pickIfcElement(event: PointerEvent): Promise<void> {
  if (!state.ifcModel) {
    return;
  }
  // Fragments.raycast (screenToCast) ожидает координаты курсора в CSS-пикселях
  // относительно страницы (clientX/clientY) — оно само вычитает rect и нормирует
  // в NDC. Передача готового NDC давала промах (raycast → null).
  pointerNdc.x = event.clientX;
  pointerNdc.y = event.clientY;

  const hit = await fragmentsRaycast(getActiveCamera(), pointerNdc, dom.canvas);
  const expressId: number | null | undefined = hit?.localId ?? null;

  if (expressId === undefined || expressId === null) {
    clearSelection();
    return;
  }
  if (state.hiddenExpressIds.has(expressId)) {
    return;
  }
  if (!isPassingFilters(expressId)) {
    return;
  }

  const multi = event.ctrlKey || event.metaKey || state.multiSelectMode;
  if (multi) {
    if (state.selectedExpressIds.has(expressId)) {
      state.selectedExpressIds.delete(expressId);
    } else {
      state.selectedExpressIds.add(expressId);
    }
  } else {
    state.selectedExpressIds.clear();
    state.selectedExpressIds.add(expressId);
  }

  updateVisibilityButtons();

  if (state.selectedExpressIds.size === 0) {
    try {
      ifcLoader.ifcManager.removeSubset(state.ifcModel.modelID, highlightMaterial, PICK_CUSTOM_ID);
    } catch (_) {
      /* ignore */
    }
    setSelectionCaption(null);
    updateAnnotationUi();
    await refreshPropertiesPanel();
    renderIfcTree();
    return;
  }

  updatePickHighlight();
  updateAnnotationUi();
  await refreshSelectionInfo();
  await refreshPropertiesPanel();
  renderIfcTree();
}

export function hideSelectedElement(): void {
  if (!state.ifcModel || state.selectedExpressIds.size === 0) {
    return;
  }
  const added = [...state.selectedExpressIds];
  for (const id of added) {
    state.hiddenExpressIds.add(id);
  }
  try {
    ifcLoader.ifcManager.removeSubset(state.ifcModel.modelID, highlightMaterial, PICK_CUSTOM_ID);
  } catch (_) {
    /* ignore */
  }
  state.selectedExpressIds.clear();
  setSelectionCaption(null);
  rebuildMeshVisibility();
  updateVisibilityButtons();
  updateAnnotationUi();
  setStatus(
    `Скрыто элементов: ${added.length}. Всего в режиме «скрыто»: ${state.hiddenExpressIds.size}.`,
  );
}

export function isolateSelectedElements(): void {
  if (!state.ifcModel || state.selectedExpressIds.size === 0) {
    return;
  }
  const keep = new Set<number>(state.selectedExpressIds);
  const allIds = getGeometryExpressIds();
  for (const id of allIds) {
    if (!keep.has(id)) {
      state.hiddenExpressIds.add(id);
    } else {
      state.hiddenExpressIds.delete(id);
    }
  }
  rebuildMeshVisibility();
  updateVisibilityButtons();
  setStatus(
    `Изолировано элементов: ${keep.size}. Скрыто: ${state.hiddenExpressIds.size}.`,
  );
}

export function showAllHiddenElements(): void {
  if (!state.ifcModel || state.hiddenExpressIds.size === 0) {
    return;
  }
  state.hiddenExpressIds.clear();
  try {
    ifcLoader.ifcManager.removeSubset(state.ifcModel.modelID, ghostMaterial, HIDDEN_GHOST_ID);
  } catch (_) {
    /* ignore */
  }
  rebuildMeshVisibility();
  clearSelection();
  updateVisibilityButtons();
  setStatus("Все скрытые элементы снова видны.");
}
